import { Component, Inject, OnInit } from '@angular/core';
import { BaseCustomDialog } from '../base-custom-dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { DateTimeHelper, TimePeriodType } from '../../../../helper/date-time.helper';
import { ActionIconHelper } from '../../../../helper/action-icon.helper';
import {LEADS_DATA_SERVICE, LeadsDataService} from '../../../../services/leads/leads-data.service';
import { ICompanyContacts, IContact, ProfileStudio } from '../../../../models/leads/leads.model';
import {AppointmentsHelper} from '../../../../helper/appointments.helper';
import { PROFILE_DATA_SERVICE, ProfileDataService } from '../../../../services/profile/profile-data.service';
import { LAST_SELECTED_STUDIO_ID, PROFILE_DATA_ID } from '../../../../constants/constants';

@Component({
  selector: 'fi-invite-planner-popup',
  templateUrl: './invite-planner-popup.component.html',
  styleUrls: ['./invite-planner-popup.component.scss'],
  standalone: false,
})
export class InvitePlannerPopupComponent extends BaseCustomDialog implements OnInit {
  contactName = '';
  leadId: number;
  selectedTypeId: number;
  readonly dateOptionTypes = DateTimeHelper.datePeriodTypes;
  searchedText = '';
  note = '';
  selectedDate: Date;
  title: string;
  iconUrl = ActionIconHelper.getActionIconUrl('PLANNER_INVITE', false);
  darkIconUrl = ActionIconHelper.getActionIconUrl('PLANNER_INVITE', true);
  submitting: boolean;
  companyContacts: ICompanyContacts[];
  contactToDisplay: IContact;
  appointmentExists = false;
  inValidNote = false;
  showroomTypes: ProfileStudio[];
  showroomTypeId: string;
  isPlanerInviteMove = false;
  isExternal = false;
  appointmentTypeId = -1;

  readonly studioToText = (studio: ProfileStudio) => {
    return [studio.KKZ, studio.Name, studio.City].filter(x => x?.trim()).join(' - ');
  }


  constructor(
    public dialogRef: MatDialogRef<InvitePlannerPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: { contactName: string, leadId: number, leadTypeId: number, title: string, isPlanerInviteMove?: boolean, isExternal?: boolean },
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    @Inject(PROFILE_DATA_SERVICE) private readonly profileDataService: ProfileDataService,
  ) {
    super(dialogRef);

    this.contactName = dialogData?.contactName || '';
    this.leadId = dialogData?.leadId;
    this.title = dialogData?.title;
    this.isPlanerInviteMove = dialogData?.isPlanerInviteMove;
    this.isExternal = dialogData?.isExternal;

    actionDataService.getAppointmentTypes().subscribe(({success, data}) => {
      if (success) {
        this.appointmentTypeId = data.find(item => item.Name === 'SCHAURAUM')?.Id ?? -1;
      }
    });

    this.profileDataService.getStudios().subscribe(({success, data}) => {
      if (success) {
        this.showroomTypes = data;
        if (!this.isExternal) {
          this.showroomTypeId = sessionStorage.getItem(LAST_SELECTED_STUDIO_ID) ?? '';
        }
      }
    });

    this.leadsDataService.getLeadById(this.leadId).subscribe(({success, data}) => {
      if (success) {
        this.companyContacts = data.CompanyContacts;
        this.contactToDisplay = data.Contact;
      }
    });
  }

  onDateSelect(date: Date, time = false): void {
    this.selectedTypeId = null;

    if (!date) {
      this.selectedDate = null;
      return;
    }

    if (!this.selectedDate) {
      this.selectedDate = date;
    } else if (!time) {
      this.selectedDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    } else {
      this.selectedDate.setHours(date.getHours(), date.getMinutes());
    }

    this.selectedDate = new Date(this.selectedDate);

    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  setPeriods($event): void {
    this.selectedTypeId = $event;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(this.dateOptionTypes.find(item => item.Id === +$event));
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  onDateOptionSelected(type: TimePeriodType): void {
    this.selectedTypeId = type.Id;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(type);
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  async ngOnInit(): Promise<void> {
    const userId = await sessionStorage.getItem(PROFILE_DATA_ID);
    AppointmentsHelper.getDbAppointments(this.actionDataService, userId);
  }

  closeDialog(): void {
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  get formIsValid(): boolean {
    if (this.isExternal) {
      return this.appointmentTypeId !== -1 && !!this.leadId && !!this.selectedDate && !this.submitting && !this.inValidNote;
    }
    return this.appointmentTypeId !== -1 && !!this.showroomTypeId && !!this.leadId && !!this.selectedDate && !this.submitting && !this.inValidNote;
  }

  inValidMaxLength(): boolean {
    this.inValidNote = this.note.length > 2000;
    return this.inValidNote;
  }

  submit(): void {
    if (this.formIsValid) {
      this.submitting = true;
      this.actionDataService.invitePlanner(
        this.leadId,
        this.note,
        this.selectedDate,
        this.showroomTypeId,
        this.appointmentTypeId,
        this.isPlanerInviteMove,
        this.isExternal
      )
          .subscribe(({success, data}) => {
            if (success) {
              this.clearClassAndCloseDialog(this.dialogRef, 'remove');
            }
            if (!this.isExternal) {
              sessionStorage.setItem(LAST_SELECTED_STUDIO_ID, this.showroomTypeId);
            }

            this.submitting = false;
          });
    }
  }
}
